import { generateGuid } from "@amx/ux-twoui-utils";
import React, { useContext, useMemo, useReducer } from "react";

export interface DialogControlProviderContextType {
    /**
     * Open the given dialog with given id or generated id.
     * Returns the given id or generated id.
     */
    readonly openDialog: (dialog: React.ReactNode, id?: string) => string;
    /**
     * Close the dialog with given id. If id is not provided then close the top
     * dialog.
     */
    readonly closeDialog: (id?: string) => void;
    /**
     * Close the current top dialog and open the given dialog with given id or
     * generated id. Returns the given id or generated id.
     */
    readonly switchToDialog: (dialog: React.ReactNode, id?: string) => string;
}

export const DialogControlProviderContext = React.createContext<DialogControlProviderContextType>({
    openDialog: (_: React.ReactNode, _2?: string): string => {
        return generateGuid();
    },
    closeDialog: (_?: string) => {
        return;
    },
    switchToDialog: (_: React.ReactNode, _2?: string): string => {
        return generateGuid();
    }
});

interface ControlledDialog {
    readonly id: string;
    readonly controlledDialog: React.ReactNode;
}

interface ControlledDialogOpenAction {
    readonly type: "openDialog";
    readonly dialog: React.ReactNode;
    readonly id: string;
}

interface ControlledDialogCloseAction {
    readonly type: "closeDialog";
    readonly id?: string;
}

type ControlledDialogAction = ControlledDialogOpenAction | ControlledDialogCloseAction;

const controlledDialogReducer = (controlledDialogs: readonly ControlledDialog[], action: ControlledDialogAction) => {
    switch (action.type) {
        case "closeDialog":
            if (!action.id) {
                // pop the top of the list
                if (controlledDialogs.length > 0) {
                    return controlledDialogs.slice(1);
                }
            }
            return controlledDialogs.filter((controlledDialog: ControlledDialog) => {
                return controlledDialog.id !== action.id;
            });
        case "openDialog":
            return ([{
                id: action.id,
                controlledDialog: action.dialog
            }, ...controlledDialogs.filter(dialog => dialog.id !== action.id)]);
        default:
            return controlledDialogs;
    }
};

export const DialogControlProvider: React.FC = ({ children }) => {

    const [controlledDialogs, dispatch] = useReducer(controlledDialogReducer, []);

    const value = useMemo(() => {
        const openDialog = (dialog: React.ReactNode, id?: string): string => {
            let dialogId = id;
            if (!dialogId) {
                dialogId = generateGuid();
            }

            dispatch({
                type: "openDialog",
                dialog,
                id: dialogId
            });

            return dialogId;
        };

        const closeDialog = (id?: string) => {
            dispatch({
                type: "closeDialog",
                id
            });
        };

        const switchToDialog = (dialog: React.ReactNode, id?: string): string => {
            closeDialog();
            return openDialog(dialog, id);
        };

        return {
            openDialog,
            closeDialog,
            switchToDialog
        };
    }, [dispatch]);

    const dialogs = controlledDialogs.map((controlledDialog: ControlledDialog) => {
        return (
            <div key={controlledDialog.id}>
                {controlledDialog.controlledDialog}
            </div>
        );
    });

    return (
        <DialogControlProviderContext.Provider value={value}>
            {children}
            {dialogs}
        </DialogControlProviderContext.Provider>
    );
};

export const useDialogController = () => {
    return useContext(DialogControlProviderContext);
};
